import React from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { InternalCheckbox, LabelText } from '~/internal';
import { handleFieldsetBlur, useId } from '~/utils';
import { useFullTheme } from '~/design';

interface CheckboxGroupContextProps {
  isDisabled: (disabled: boolean) => boolean;
  isChecked: (value: string) => boolean;
  toggleValue: (checked: boolean, value: string) => void;
}

export const CheckboxGroupContext = React.createContext<CheckboxGroupContextProps | null>(null);

export interface CheckboxGroupProps extends FieldCompatibleProps<string[]> {
  /** If true, all of the checkboxes cannot be interacted with */
  disabled?: boolean;

  /** The content of the checkbox group that includes one or more `<CheckboxGroup.Option/>` */
  children: React.ReactNode;
}

/**
 * CheckboxGroup allows the user to select multiple options at once.
 * - If you want to allow selecting only one option, avoid using this component and use the RadioGroup component instead.
 * - If there are more than 5 options, it's recommended to use the Select or Combobox components instead.
 */
export function CheckboxGroup({
  disabled = false,
  helpText,
  invalid,
  label,
  onBlur,
  onChange,
  children,
  required = false,
  value,
}: CheckboxGroupProps) {
  const labelId = useId();
  const helpTextId = useId();
  const { spacing } = useFullTheme();
  const { isDisabled, isChecked, toggleValue } = useCheckboxGroup({ disabled, value, onChange });

  return (
    // This is fine to disable because we're reacting to blurs from child interactive elements
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <fieldset
      aria-describedby={helpText ? helpTextId : undefined}
      aria-labelledby={label ? labelId : undefined}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[2],
        border: 0,
        marginInline: 0,
        padding: 0,
      }}
      onBlur={event => handleFieldsetBlur({ event, onBlur })}
    >
      {label && (
        <LabelText as="legend" disabled={disabled} id={labelId} required={required}>
          {label}
        </LabelText>
      )}

      {helpText && (
        <HelpText id={helpTextId} intent={invalid ? 'negative' : 'secondary'} showIcon={invalid}>
          {helpText}
        </HelpText>
      )}

      <CheckboxGroupContext.Provider
        value={{
          isDisabled,
          isChecked,
          toggleValue,
        }}
      >
        {children}
      </CheckboxGroupContext.Provider>
    </fieldset>
  );
}

interface CheckboxGroupOptionProps {
  /** The text of the checkbox. Avoid adding interactive elements inside */
  label: string;

  /** The value of the checkbox */
  value: string;

  /** If true, this checkbox cannot be interacted with */
  disabled?: boolean;

  /** If true, the checkbox button appears indeterminate */
  indeterminate?: boolean;
}

CheckboxGroup.Option = function CheckboxGroupOption({
  label,
  value,
  disabled = false,
  indeterminate = false,
}: CheckboxGroupOptionProps) {
  const context = React.useContext(CheckboxGroupContext);

  if (!context) {
    throw new Error('`CheckboxGroup.Option` can only be rendered inside `CheckboxGroup`');
  }

  const { isDisabled, isChecked, toggleValue } = context;

  return (
    <InternalCheckbox
      label={label}
      checked={isChecked(value)}
      disabled={isDisabled(disabled)}
      indeterminate={indeterminate}
      onChange={({ target: { checked } }) => toggleValue(checked, value)}
    />
  );
};

function useCheckboxGroup({
  disabled: groupDisabled,
  value: checkedValues,
  onChange: onCheckedValuesChange,
}: {
  disabled: boolean;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const isDisabled = (disabled: boolean) => disabled || groupDisabled;

  const isChecked = (value: string) => checkedValues.includes(value);

  const toggleValue = (checked: boolean, value: string) => {
    if (checked) {
      onCheckedValuesChange(checkedValues.concat(value));
    } else {
      onCheckedValuesChange(checkedValues.filter(v => v !== value));
    }
  };

  return { isDisabled, isChecked, toggleValue };
}
